import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFavorites } from "@/hooks/useFavorites";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/data/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, BellRing, Home as HomeIcon, CheckCheck, X, TrendingDown } from "lucide-react";

const NOTIF_KEY = "rento_notifications";
const SEEN_KEY = "rento_fav_status";

function readNotifs() {
  try {
    return JSON.parse(localStorage.getItem(NOTIF_KEY) || "[]");
  } catch {
    return [];
  }
}

function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export const NotificationBell = () => {
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState(readNotifs);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const [serverAlerts, setServerAlerts] = useState([]);

  const loadServer = useCallback(async () => {
    if (!user) { setServerAlerts([]); return; }
    try { const { data } = await api.get("/my/match-alerts"); setServerAlerts(data); } catch { /* noop */ }
  }, [user]);

  useEffect(() => {
    loadServer();
    const t = setInterval(loadServer, 30000);
    return () => clearInterval(t);
  }, [loadServer]);

  const persist = useCallback((next) => {
    localStorage.setItem(NOTIF_KEY, JSON.stringify(next));
    setNotifs(next);
  }, []);

  const check = useCallback(async () => {
    // Always prune notifications for listings no longer saved (even when none are saved)
    const favSet = new Set(favorites);
    const existing = readNotifs();
    const prunedExisting = existing.filter((n) => favSet.has(n.listing_id));
    if (prunedExisting.length !== existing.length) persist(prunedExisting);
    // Keep the status memo scoped to currently-saved listings
    const memo = JSON.parse(localStorage.getItem(SEEN_KEY) || "{}");
    let memoChanged = false;
    Object.keys(memo).forEach((id) => {
      if (!favSet.has(id)) { delete memo[id]; memoChanged = true; }
    });
    if (memoChanged) localStorage.setItem(SEEN_KEY, JSON.stringify(memo));
    if (!favorites.length) return;
    try {
      const { data } = await api.post("/listings/statuses", { ids: favorites });
      const seen = JSON.parse(localStorage.getItem(SEEN_KEY) || "{}");
      let current = readNotifs();
      let changed = false;
      for (const l of data) {
        const prev = seen[l.id];
        const prevObj = prev && typeof prev === "object" ? prev : null;
        // Status changed to rented/sold
        const done = l.status === "rented" || l.status === "sold";
        if (done && prevObj && prevObj.status !== l.status &&
            !current.some((n) => n.listing_id === l.id && n.type === "status" && n.status === l.status)) {
          current = [
            { id: `${l.id}-${l.status}-${Date.now()}`, type: "status", listing_id: l.id, title: l.title, status: l.status, ts: Date.now(), read: false },
            ...current,
          ];
          changed = true;
        }
        // Price reduced
        if (prevObj && typeof l.price === "number" && typeof prevObj.price === "number" && l.price < prevObj.price) {
          current = [
            { id: `${l.id}-price-${Date.now()}`, type: "price", listing_id: l.id, title: l.title, oldPrice: prevObj.price, price: l.price, currency: l.currency || "KSH", ts: Date.now(), read: false },
            ...current,
          ];
          changed = true;
        }
        seen[l.id] = { status: l.status, price: l.price };
      }
      localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
      if (changed) persist(current);
    } catch {
      /* offline / transient — retry next tick */
    }
  }, [favorites, persist]);

  useEffect(() => {
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
  }, [check]);

  const unread = notifs.filter((n) => !n.read).length + serverAlerts.filter((a) => !a.read).length;

  const markAllRead = () => {
    persist(notifs.map((n) => ({ ...n, read: true })));
    if (serverAlerts.some((a) => !a.read)) {
      api.patch("/my/match-alerts/read").catch(() => {});
      setServerAlerts((p) => p.map((a) => ({ ...a, read: true })));
    }
  };
  const clearAll = () => persist([]);
  const openNotif = (n) => {
    persist(notifs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setOpen(false);
    navigate(`/listing/${n.listing_id}`);
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v && unread) markAllRead(); }}>
      <PopoverTrigger asChild>
        <Button
          data-testid="notif-bell"
          variant="ghost"
          size="icon"
          className="relative rounded-full text-[#2C3830] hover:bg-[#2C3830]/5"
          title="Notifications"
        >
          {unread > 0 ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
          {unread > 0 && (
            <span
              data-testid="notif-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#D4A373] px-1 text-[10px] font-bold text-white"
            >
              {unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent data-testid="notif-panel" align="end" className="w-80 rounded-2xl p-0">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <p className="font-display text-base font-medium text-[#2C3830]">Notifications</p>
          {notifs.length > 0 && (
            <button data-testid="notif-clear" onClick={clearAll} className="flex items-center gap-1 text-xs text-[#6B7280] hover:text-[#2C3830]">
              <X className="h-3 w-3" /> Clear
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto rento-scroll-hide">
          {serverAlerts.map((a) => (
            <button
              key={a.id}
              data-testid={`match-alert-${a.listing_id}`}
              onClick={() => { setOpen(false); navigate(`/listing/${a.listing_id}`); }}
              className={`flex w-full items-start gap-3 border-b border-border/40 px-4 py-3 text-left transition-colors hover:bg-[#F7F5F0] ${a.read ? "" : "bg-[#1D4ED8]/5"}`}
            >
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1D4ED8] text-white"><HomeIcon className="h-4 w-4" /></span>
              <span className="flex-1">
                <span className="block text-sm text-[#1A1A1A]">New space near <span className="font-medium">{a.institution}</span>: {a.listing_title}</span>
                <span className="mt-0.5 block text-xs text-[#6B7280]">Matches your room search</span>
              </span>
              {!a.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#1D4ED8]" />}
            </button>
          ))}
          {notifs.length === 0 && serverAlerts.length === 0 ? (
            <div data-testid="notif-empty" className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <CheckCheck className="h-7 w-7 text-[#D4A373]" />
              <p className="text-sm text-[#6B7280]">You're all caught up. We'll alert you here when a saved property is rented, sold, or drops in price.</p>
            </div>
          ) : (
            notifs.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-item-${n.listing_id}`}
                onClick={() => openNotif(n)}
                className={`flex w-full items-start gap-3 border-b border-border/40 px-4 py-3 text-left transition-colors hover:bg-[#F7F5F0] ${n.read ? "" : "bg-[#D4A373]/5"}`}
              >
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white ${n.type === "price" ? "bg-[#25D366]" : "bg-[#2C3830]"}`}>
                  {n.type === "price" ? <TrendingDown className="h-4 w-4" /> : <HomeIcon className="h-4 w-4" />}
                </span>
                <span className="flex-1">
                  {n.type === "price" ? (
                    <span className="block text-sm text-[#1A1A1A]">
                      <span className="font-medium">{n.title}</span> price dropped to{" "}
                      <span className="font-medium text-[#25D366]">{money(n.price, n.currency)}</span>{" "}
                      <span className="text-[#6B7280] line-through">{money(n.oldPrice, n.currency)}</span>
                    </span>
                  ) : (
                    <span className="block text-sm text-[#1A1A1A]">
                      <span className="font-medium">{n.title}</span> has been{" "}
                      <span className={n.status === "sold" ? "text-[#B91C1C] font-medium" : "text-[#B45309] font-medium"}>{n.status}</span>.
                    </span>
                  )}
                  <span className="mt-0.5 block text-xs text-[#6B7280]">{timeAgo(n.ts)}</span>
                </span>
                {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#D4A373]" />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
